import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WarningCircleIcon } from '@phosphor-icons/react/dist/ssr';
import RawLogText from '@/components/common/RawLogText';
import type { PatchType } from 'shared/types';

export type LogEntry = Extract<
  PatchType,
  { type: 'STDOUT' } | { type: 'STDERR' }
>;

export interface VirtualizedProcessLogsProps {
  logs: LogEntry[];
  error: string | null;
  searchQuery: string;
  matchIndices: number[];
  currentMatchIndex: number;
}

type LogEntryWithKey = LogEntry & { key: string; originalIndex: number };

interface SearchContext {
  searchQuery: string;
  matchIndices: number[];
  currentMatchIndex: number;
}

const ItemContent = ({
  data,
  context,
}: {
  data: LogEntryWithKey;
  context: SearchContext;
}) => {
  const isMatch = context.matchIndices.includes(data.originalIndex);
  const isCurrentMatch =
    context.matchIndices[context.currentMatchIndex] === data.originalIndex;

  return (
    <RawLogText
      content={data.content}
      channel={data.type === 'STDERR' ? 'stderr' : 'stdout'}
      className="text-sm px-4 py-1"
      linkifyUrls
      searchQuery={isMatch ? context.searchQuery : undefined}
      isCurrentMatch={isCurrentMatch}
    />
  );
};

export function VirtualizedProcessLogs({
  logs,
  error,
  searchQuery,
  matchIndices,
  currentMatchIndex,
}: VirtualizedProcessLogsProps) {
  const { t } = useTranslation('tasks');
  const [entries, setEntries] = useState<LogEntryWithKey[]>([]);
  const [atBottom, setAtBottom] = useState(true);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const prevEntriesLengthRef = useRef(0);
  const prevCurrentMatchRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const logsWithKeys: LogEntryWithKey[] = logs.map((entry, index) => ({
      ...entry,
      key: `log-${index}`,
      originalIndex: index,
    }));

    setEntries(logsWithKeys);
  }, [logs]);

  // Handle initial scroll to bottom and smooth scrolling for new logs
  useEffect(() => {
    if (entries.length === 0) return;

    const prevLength = prevEntriesLengthRef.current;
    const hasNewEntries = entries.length > prevLength;

    if (prevLength === 0 || (hasNewEntries && atBottom)) {
      requestAnimationFrame(() => {
        virtuosoRef.current?.scrollToIndex({
          index: entries.length - 1,
          align: 'end',
          behavior: hasNewEntries ? 'smooth' : 'auto',
        });
      });
    }

    prevEntriesLengthRef.current = entries.length;
  }, [entries.length, atBottom, entries]);

  // Scroll to current match when it changes
  useEffect(() => {
    if (
      matchIndices.length > 0 &&
      currentMatchIndex >= 0 &&
      currentMatchIndex !== prevCurrentMatchRef.current
    ) {
      const logIndex = matchIndices[currentMatchIndex];
      const targetIndex = entries.findIndex(
        (entry) => entry.originalIndex === logIndex
      );

      if (targetIndex >= 0) {
        virtuosoRef.current?.scrollToIndex({
          index: targetIndex,
          align: 'center',
          behavior: 'smooth',
        });
      }
      prevCurrentMatchRef.current = currentMatchIndex;
    }
  }, [currentMatchIndex, matchIndices, entries]);

  if (logs.length === 0 && !error) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-center text-muted-foreground text-sm">
          {t('processes.noLogsAvailable')}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-center text-destructive text-sm">
          <WarningCircleIcon className="size-icon-base inline mr-2" />
          {error}
        </p>
      </div>
    );
  }

  const context: SearchContext = {
    searchQuery,
    matchIndices,
    currentMatchIndex,
  };

  return (
    <div className="h-full">
      <Virtuoso<LogEntryWithKey>
        ref={virtuosoRef}
        className="h-full"
        data={entries}
        computeItemKey={(_, data) => data.key}
        itemContent={(_, data) => <ItemContent data={data} context={context} />}
        atBottomStateChange={setAtBottom}
        followOutput={atBottom ? 'smooth' : false}
        increaseViewportBy={{ top: 200, bottom: 200 }}
      />
    </div>
  );
}
